import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'utils/i18n';
import { SUPPORTED_LANGUAGES, getLanguageByCode } from 'utils/languages';
import './LanguageExpander.css';

// Language icon component
const LanguageIcon = ({ size = 18 }) => {
  return (
    <svg 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      width={size}
      height={size}
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  );
};

/**
 * LanguageExpander Component
 * Expandable language selector for mobile sidebar
 * Animates to reveal scrollable language list
 */
const LanguageExpander = ({ className = '' }) => {
  const { language, changeLanguage, t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  // Calculate content height when expanded state changes
  useEffect(() => {
    if (isExpanded && contentRef.current) {
      const height = contentRef.current.scrollHeight;
      setContentHeight(height);
    } else {
      setContentHeight(0);
    }
  }, [isExpanded]);

  const currentLangInfo = getLanguageByCode(language);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleLanguageSelect = (langCode) => {
    changeLanguage(langCode);
    setIsExpanded(false);
  };

  return (
    <div className={`language-expander ${isExpanded ? 'expanded' : ''} ${className}`.trim()}>
      {/* Trigger Button */}
      <button
        className="language-expander-trigger"
        onClick={handleToggle}
        aria-expanded={isExpanded}
        aria-label={t('languageSelector.selectLanguage', 'Select Language')}
      >
        <span className="language-expander-icon">
          <LanguageIcon size={18} />
        </span>
        <span className="language-expander-label">
          {currentLangInfo ? currentLangInfo.nativeName : language.toUpperCase()}
        </span>
        <span className={`language-expander-arrow ${isExpanded ? 'expanded' : ''}`}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            width="16"
            height="16"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </span>
      </button>

      {/* Expandable Content */}
      <div
        className="language-expander-content"
        style={{
          maxHeight: isExpanded ? `${contentHeight}px` : '0px',
          opacity: isExpanded ? 1 : 0,
        }}
        ref={contentRef}
      >
        <div className="language-expander-list">
          {SUPPORTED_LANGUAGES.map((langInfo) => {
            const isSelected = langInfo.code === language;
            
            return (
              <button
                key={langInfo.code}
                className={`language-expander-item ${isSelected ? 'selected' : ''}`}
                onClick={() => handleLanguageSelect(langInfo.code)}
                aria-label={langInfo.nativeName}
                aria-pressed={isSelected}
              >
                <div className="language-expander-item-content">
                  <LanguageIcon size={16} />
                  <span className="language-expander-item-name">
                    {langInfo.nativeName}
                  </span>
                </div>
                {isSelected && (
                  <span className="language-expander-item-check" aria-hidden="true">
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LanguageExpander;

